// "Sheet Material" nesting step — picks a shape (picture-frame / rectangle /
// circle / standoff), gathers shape-specific dimensions, packs the part on a
// phenolic sheet, and renders the result in an SVG.
//
// All four shapes flow through the same rectangle-pack math (`calcCollarNesting`
// in src/shared/collarMath.ts). The shape just decides what BOUNDING BOX to
// feed the packer:
//   - frame      → outsideLen × outsideWid (the picture-frame outer)
//   - rectangle  → outsideLen × outsideWid (solid plate; no inner cutout)
//   - circle     → diameter × diameter (over-estimate — we pack the disk's
//                  enclosing square. Acceptable for KMF quick-quote intent;
//                  documented in the UI hint.)
//   - standoff   → legLen × height (C-shape's bounding-box footprint;
//                  thickness only affects the SVG render, not the nest count.)
//
// Persistence: shape goes into part.collar.shape, the standoff sub-inputs into
// part.collar.standoff, and the circle diameter into part.collar.diameter.
// outsideLen/Wid/frameWidth are reused across shapes where they map cleanly
// (outsideLen=length, outsideWid=width) so the cut-time step still has the
// numbers it needs.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { CollarSpec } from '@/shared/crmTypes';
import { calcCollarNesting, COLLAR_DEFAULTS } from '@/shared/collarMath';

// Hex for the live-readout accent. Mirrors v1's #0891B2 cyan. We pull this
// inline rather than reading the Tailwind config because Tailwind class
// strings can't be templated for arbitrary text-color targets.
const ACCENT = '#0891B2';

type NestShape = NonNullable<CollarSpec['shape']>;

interface ShapeOption {
  id: NestShape;
  label: string;
  icon: string;
}

const SHAPES: ShapeOption[] = [
  { id: 'frame', label: 'Frame', icon: '🖼' },
  { id: 'rectangle', label: 'Rectangle', icon: '▭' },
  { id: 'circle', label: 'Circle', icon: '⭕' },
  { id: 'standoff', label: 'Standoff', icon: '⊏' },
];

export function CollarNestingStep({ part, onChange }: StepProps) {
  const collar: Partial<CollarSpec> = part.collar ?? {};

  // ─── Resolve every field against the defaults so the readout shows real
  //     numbers on a fresh part (before the user has touched anything). ───
  const shape: NestShape = collar.shape ?? 'frame';
  const outsideLen = collar.outsideLen ?? 30;
  const outsideWid = collar.outsideWid ?? 10;
  const frameWidth = collar.frameWidth ?? 2;
  const diameter = collar.diameter ?? 6;
  const standoff = collar.standoff ?? { legLen: 2, height: 3.5, thickness: 0.25 };
  const sheetLen = collar.sheetLen ?? COLLAR_DEFAULTS.sheetLen;
  const sheetWid = collar.sheetWid ?? COLLAR_DEFAULTS.sheetWid;
  const spacing = collar.spacing ?? COLLAR_DEFAULTS.spacing;

  function patch(updates: Partial<CollarSpec>) {
    // Spread-merge against the existing collar block so partial updates
    // don't clobber sibling fields (cut-step inputs etc).
    onChange({ collar: { outsideLen, outsideWid, frameWidth, ...collar, ...updates } });
  }

  function pickShape(next: NestShape) {
    patch({ shape: next });
  }

  function pickSheet(len: number, wid: number) {
    patch({ sheetLen: len, sheetWid: wid });
  }

  // ─── Per-shape bounding box for the packer ───────────────────────────
  // The math layer is a generic rectangle packer; each shape just feeds it
  // the right footprint. Documented per-shape next to the call site.
  let packLen = outsideLen;
  let packWid = outsideWid;
  if (shape === 'circle') {
    packLen = diameter;
    packWid = diameter;
  } else if (shape === 'standoff') {
    packLen = standoff.legLen;
    packWid = standoff.height;
  }

  const nest = calcCollarNesting(packLen, packWid, sheetLen, sheetWid, spacing);
  const qty = part.qtyTiers?.[0] ?? 1;
  const sheetsNeeded = nest.count > 0 ? Math.ceil(qty / nest.count) : 0;
  const spareSlots = nest.count > 0 ? sheetsNeeded * nest.count - qty : 0;

  // Sheet-size presets (one-click chips). Defined in collarMath.ts so the
  // chip set stays in sync with the math layer's known sizes.
  const presets = COLLAR_DEFAULTS.sheetPresets;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* ─────────── LEFT: inputs ─────────── */}
      <div className="space-y-4">
        {/* Shape picker — the new top-of-step control. Lives above the
            geometry block so dimension labels can react to it. */}
        <section>
          <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide mb-2">
            Shape
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {SHAPES.map((s) => {
              const on = shape === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pickShape(s.id)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded border-2 transition-colors flex items-center gap-1.5 ${
                    on
                      ? 'border-orange bg-orange/10 text-orange'
                      : 'border-line-dark bg-paper text-text hover:border-orange'
                  }`}
                  aria-pressed={on}
                >
                  <span aria-hidden>{s.icon}</span>
                  <span>{s.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Geometry block — shape-driven inputs. */}
        <section>
          <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide mb-2">
            {shape === 'frame' && 'Frame geometry'}
            {shape === 'rectangle' && 'Rectangle geometry'}
            {shape === 'circle' && 'Circle geometry'}
            {shape === 'standoff' && 'Standoff geometry'}
          </h3>
          <div className="space-y-3">
            {shape === 'frame' && (
              <>
                <NumberSlider
                  label="Outside length"
                  unit="in"
                  value={outsideLen}
                  min={1}
                  max={96}
                  step={0.25}
                  onChange={(v) => patch({ outsideLen: v })}
                />
                <NumberSlider
                  label="Outside width"
                  unit="in"
                  value={outsideWid}
                  min={1}
                  max={48}
                  step={0.25}
                  onChange={(v) => patch({ outsideWid: v })}
                />
                <NumberSlider
                  label="Frame border width"
                  unit="in"
                  value={frameWidth}
                  min={0.125}
                  max={6}
                  step={0.125}
                  hint='picture-frame border (~2" typical)'
                  onChange={(v) => patch({ frameWidth: v })}
                />
              </>
            )}

            {shape === 'rectangle' && (
              <>
                <NumberSlider
                  label="Outside length"
                  unit="in"
                  value={outsideLen}
                  min={1}
                  max={96}
                  step={0.25}
                  onChange={(v) => patch({ outsideLen: v })}
                />
                <NumberSlider
                  label="Outside width"
                  unit="in"
                  value={outsideWid}
                  min={1}
                  max={48}
                  step={0.25}
                  onChange={(v) => patch({ outsideWid: v })}
                />
                <div className="text-[11px] text-text-mute italic">
                  Solid plate — no inner cutout. Cut-time step uses outer
                  perimeter only.
                </div>
              </>
            )}

            {shape === 'circle' && (
              <>
                <NumberSlider
                  label="Diameter"
                  unit="in"
                  value={diameter}
                  min={0.5}
                  max={48}
                  step={0.125}
                  onChange={(v) => patch({ diameter: v })}
                />
                <div className="text-[11px] text-text-mute italic">
                  Nesting treats each disk as a square of side = diameter — over-estimates sheet usage by ~21%. Good enough for quick-quote sizing.
                </div>
              </>
            )}

            {shape === 'standoff' && (
              <>
                <NumberSlider
                  label="Leg length"
                  unit="in"
                  value={standoff.legLen}
                  min={0.25}
                  max={12}
                  step={0.125}
                  hint="depth of the C — typically ~2 in"
                  onChange={(v) =>
                    patch({ standoff: { ...standoff, legLen: v } })
                  }
                />
                <NumberSlider
                  label="Height"
                  unit="in"
                  value={standoff.height}
                  min={0.5}
                  max={24}
                  step={0.125}
                  hint="overall height of the C — typically 3-4 in"
                  onChange={(v) =>
                    patch({ standoff: { ...standoff, height: v } })
                  }
                />
                <NumberSlider
                  label="Thickness"
                  unit="in"
                  value={standoff.thickness}
                  min={0.0625}
                  max={2}
                  step={0.0625}
                  hint="material thickness (also drawn as web/leg thickness)"
                  onChange={(v) =>
                    patch({ standoff: { ...standoff, thickness: v } })
                  }
                />
                <div className="text-[11px] text-text-mute italic">
                  Nests as a {standoff.legLen.toFixed(2)} × {standoff.height.toFixed(2)} in
                  rectangle (the C-shape's bounding box).
                </div>
              </>
            )}

            <NumberSlider
              label="Part-to-part spacing"
              unit="in"
              value={spacing}
              min={0}
              max={1}
              step={0.005}
              hint="gap between adjacent parts on the nest"
              onChange={(v) => patch({ spacing: v })}
            />
          </div>
        </section>

        <section>
          <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide mb-2">
            Sheet size
          </h3>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {presets.map((p) => {
              const on = sheetLen === p.len && sheetWid === p.wid;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => pickSheet(p.len, p.wid)}
                  className={`px-3 py-1 text-xs font-semibold rounded border-2 transition-colors ${
                    on
                      ? 'border-orange bg-orange/10 text-orange'
                      : 'border-line-dark bg-paper text-text hover:border-orange'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sheet length (in)">
              <NumberInput
                value={sheetLen}
                onChange={(v) => patch({ sheetLen: v ?? sheetLen })}
                step={1}
                numpadStep={0.0625}
                min={0}
                unit="in"
                numpadTitle="Sheet length"
              />
            </Field>
            <Field label="Sheet width (in)">
              <NumberInput
                value={sheetWid}
                onChange={(v) => patch({ sheetWid: v ?? sheetWid })}
                step={1}
                numpadStep={0.0625}
                min={0}
                unit="in"
                numpadTitle="Sheet width"
              />
            </Field>
          </div>
        </section>
      </div>

      {/* ─────────── RIGHT: live readout ─────────── */}
      <div className="space-y-3">
        <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide">
          Nesting result · live
        </h3>

        <div className="border border-line rounded p-4 bg-paper">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold leading-none" style={{ color: ACCENT }}>
              {nest.count}
            </span>
            <span className="text-sm text-text-mute">parts / sheet</span>
          </div>
          <div className="text-xs text-text-mute mt-1.5">
            {nest.cols} cols × {nest.rows} rows
            {nest.rotated && (
              <span className="text-orange font-semibold"> · rotated 90°</span>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-dashed border-line space-y-1 text-sm">
            <Row label="Qty wanted (tier 1)">
              <span className="font-mono">{qty}</span>
            </Row>
            <Row label="Sheets needed">
              <span className="font-mono font-semibold" style={{ color: ACCENT }}>
                {sheetsNeeded}
              </span>
            </Row>
            <Row label="Spare slots (last sheet)">
              <span className="font-mono text-text-mute text-xs">
                {nest.count > 0 ? spareSlots : '—'}
              </span>
            </Row>
          </div>
        </div>

        <div>
          <div className="text-[11px] text-text-mute mb-1.5">
            Visual layout · 1 sheet, scaled to fit
          </div>
          {nest.count > 0 ? (
            <NestingSvg
              cols={nest.cols}
              rows={nest.rows}
              partLen={nest.partLen}
              partWid={nest.partWid}
              sheetLen={sheetLen}
              sheetWid={sheetWid}
              shape={shape}
              frameWidth={frameWidth}
              standoffThickness={standoff.thickness}
              rotated={nest.rotated}
              color={ACCENT}
            />
          ) : (
            <div className="border border-dashed border-orange/60 bg-orange/5 rounded p-3 text-xs text-orange">
              This part doesn't fit on the selected sheet — try a larger sheet (5×10) or smaller dims.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────── SVG visualization ───────────
// Per-shape renderer. The packer gives us cols/rows/partLen/partWid for the
// chosen ORIENTATION; we just paint the right glyph inside each tile.
interface NestingSvgProps {
  cols: number;
  rows: number;
  partLen: number;
  partWid: number;
  sheetLen: number;
  sheetWid: number;
  shape: NestShape;
  frameWidth: number;
  standoffThickness: number;
  rotated: boolean;
  color: string;
}

function NestingSvg({
  cols,
  rows,
  partLen,
  partWid,
  sheetLen,
  sheetWid,
  shape,
  frameWidth,
  standoffThickness,
  rotated,
  color,
}: NestingSvgProps) {
  // Pick the scale so the longer axis fills ~560px without overflowing the
  // 280px height budget. Mirrors v1's clamps.
  const maxWidth = 560;
  const maxHeight = 280;
  const scale = Math.min(maxWidth / sheetLen, maxHeight / sheetWid);
  const svgW = sheetLen * scale;
  const svgH = sheetWid * scale;
  const fw = Math.max(0, frameWidth) * scale;
  const stThk = Math.max(0, standoffThickness) * scale;

  const tiles: React.ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = c * partLen * scale;
      const y = r * partWid * scale;
      const w = partLen * scale;
      const h = partWid * scale;
      tiles.push(
        <g key={`${r}-${c}`}>
          {renderShapeGlyph({ shape, x, y, w, h, fw, stThk, rotated, color })}
        </g>,
      );
    }
  }

  return (
    <svg
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      className="border border-line-dark rounded max-w-full"
      // Inline style: the diagonal hatched background mimics a phenolic
      // sheet, matches v1. Tailwind doesn't have a clean repeating-linear-
      // gradient utility.
      style={{
        background:
          'repeating-linear-gradient(45deg, #FAFAFA 0, #FAFAFA 8px, #F0F0F0 8px, #F0F0F0 10px)',
      }}
    >
      {tiles}
      <rect
        x={0.5}
        y={0.5}
        width={svgW - 1}
        height={svgH - 1}
        fill="none"
        stroke="#1F2937"
        strokeWidth={1.5}
      />
    </svg>
  );
}

// One tile's geometry. Pure SVG output — no math beyond local pixel coords.
interface GlyphProps {
  shape: NestShape;
  x: number;
  y: number;
  w: number;
  h: number;
  fw: number; // frame border, scaled
  stThk: number; // standoff thickness, scaled
  rotated: boolean;
  color: string;
}

function renderShapeGlyph({ shape, x, y, w, h, fw, stThk, rotated, color }: GlyphProps) {
  const fillOpacity = 0.55;
  const stroke = color;
  const fill = color;

  if (shape === 'frame') {
    const showInner = fw > 0 && w - 2 * fw > 4 && h - 2 * fw > 4;
    return (
      <>
        <rect
          x={x + 1}
          y={y + 1}
          width={w - 2}
          height={h - 2}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={stroke}
          strokeWidth={1.2}
        />
        {showInner && (
          <rect
            x={x + fw}
            y={y + fw}
            width={w - 2 * fw}
            height={h - 2 * fw}
            fill="#FFFFFF"
            stroke={stroke}
            strokeWidth={0.6}
            strokeDasharray="2 2"
          />
        )}
      </>
    );
  }

  if (shape === 'rectangle') {
    // Solid plate — just the outer rectangle.
    return (
      <rect
        x={x + 1}
        y={y + 1}
        width={w - 2}
        height={h - 2}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeWidth={1.2}
      />
    );
  }

  if (shape === 'circle') {
    // Circle inscribed in the tile's footprint — radius = min(w,h)/2.
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.max(0, Math.min(w, h) / 2 - 1);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeWidth={1.2}
      />
    );
  }

  // standoff — C-shape (3-sided rectangle missing the right side). Draw as
  // an open polyline so the open end reads as a slot. When the packer
  // rotated the part 90°, swap which side is open so the C still points
  // "out" along the original leg axis.
  // SVG path:
  //   M start at top-right · L top-left · L bottom-left · L bottom-right
  // Then a thinner inner outline to suggest the wall thickness.
  const pad = 1;
  const x0 = x + pad;
  const y0 = y + pad;
  const x1 = x + w - pad;
  const y1 = y + h - pad;

  // Outer C path. `rotated` swaps the open side from right → bottom.
  const outer = rotated
    ? `M ${x0},${y0} L ${x1},${y0} L ${x1},${y1} L ${x0},${y1}` // open at bottom
    : `M ${x1},${y0} L ${x0},${y0} L ${x0},${y1} L ${x1},${y1}`; // open at right

  // Inner C path, inset by stThk, to suggest material thickness. Skip the
  // inner outline if the thickness collapses the inset.
  const t = Math.max(0, stThk);
  const inX0 = x0 + t;
  const inY0 = y0 + t;
  const inX1 = x1 - t;
  const inY1 = y1 - t;
  const showInner = inX1 - inX0 > 4 && inY1 - inY0 > 4;
  const inner = rotated
    ? `M ${inX0},${inY0} L ${inX1},${inY0} L ${inX1},${inY1} L ${inX0},${inY1}`
    : `M ${inX1},${inY0} L ${inX0},${inY0} L ${inX0},${inY1} L ${inX1},${inY1}`;

  return (
    <>
      {/* Fill polygon — closes the C so it reads as solid material. */}
      <rect
        x={x + pad}
        y={y + pad}
        width={w - 2 * pad}
        height={h - 2 * pad}
        fill={fill}
        fillOpacity={fillOpacity * 0.5}
        stroke="none"
      />
      <path d={outer} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="miter" />
      {showInner && (
        <path
          d={inner}
          fill="none"
          stroke={stroke}
          strokeWidth={0.6}
          strokeDasharray="2 2"
        />
      )}
    </>
  );
}

// ─────────── tiny inputs ───────────
interface NumberSliderProps {
  label: string;
  unit?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  hint?: string;
  onChange: (v: number) => void;
}

function NumberSlider({ label, unit, value, min, max, step, hint, onChange }: NumberSliderProps) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] font-semibold text-text-mute uppercase tracking-wide">
          {label}
        </span>
        <span className="text-xs font-mono text-text">
          {value}
          {unit && <span className="text-text-mute"> {unit}</span>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseNumOr(e.target.value, value))}
          className="flex-1 accent-orange"
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseNumOr(e.target.value, value))}
          className="w-20 px-1.5 py-1 border border-line-dark rounded text-xs font-mono focus:outline-none focus:border-orange"
        />
      </div>
      {hint && <div className="text-[10px] text-text-mute italic mt-0.5">{hint}</div>}
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

interface RowProps {
  label: string;
  children: React.ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-mute">{label}</span>
      {children}
    </div>
  );
}

/** Parse a numeric input safely — falls back to the prior value so typing a
 *  half-finished number (".") doesn't yank the slider to 0 and disrupt focus. */
function parseNumOr(s: string, fallback: number): number {
  if (s === '' || s === '-' || s === '.') return fallback;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : fallback;
}
